import { InputDropdownListControlled } from '@nexans/react-form'
import { DropdownListItemText } from '@nexans/react-ui-kit'
import { useMemo, useState } from 'react'
import { Control, FieldValues, Path } from 'react-hook-form'

import { useGetAddressSearch } from '@/api/useGetAddressSearch'
import { AddressSuggestion } from '@/types/map'

type InputAddressSearchControlledProps<TFieldValues extends FieldValues> = {
  control: Control<TFieldValues>
  name: Path<TFieldValues>
  label: string
  errorText?: string
  gutterBottom?: boolean
  required?: boolean
  shouldUnregister?: boolean
  /**
   * Optional fallback address to pre-populate the dropdown when no address has been selected yet.
   * Typically derived from the current persisted value (e.g. site.address).
   */
  defaultAddress?: AddressSuggestion
  onAddressChange?: (address: AddressSuggestion | null) => void
}

export const InputAddressSearchControlled = <TFieldValues extends FieldValues>({
  control,
  name,
  label,
  errorText,
  gutterBottom,
  required,
  shouldUnregister,
  defaultAddress,
  onAddressChange,
}: InputAddressSearchControlledProps<TFieldValues>) => {
  const [addressInput, setAddressInput] = useState<string>('')
  const [userSelectedAddress, setUserSelectedAddress] = useState<AddressSuggestion | null>(null)

  const { data: searchResults, isFetching } = useGetAddressSearch({
    input: addressInput,
    config: { enabled: !!addressInput && addressInput.length > 2 },
  })

  // The effective "persisted" address: user selection takes priority, then the defaultAddress fallback.
  const persistedAddress = userSelectedAddress ?? defaultAddress ?? null

  const addressOptions = useMemo(() => {
    const suggestions =
      searchResults?.suggestions.map((s) => ({
        value: s.address,
        object: s,
        label: s.address,
      })) ?? []

    const extra = persistedAddress
      ? [{ value: persistedAddress.address, object: persistedAddress, label: persistedAddress.address }]
      : []

    return [...suggestions, ...extra].filter((item, index, self) => self.findIndex((i) => i.value === item.value) === index)
  }, [searchResults, persistedAddress])

  return (
    <InputDropdownListControlled
      control={control}
      errorText={errorText}
      gutterBottom={gutterBottom}
      isLoading={isFetching}
      isLoadingList={isFetching}
      keyValue="value"
      label={label}
      name={name}
      options={addressOptions}
      renderItem={(item) => <DropdownListItemText label={item.label} value={item.value} />}
      renderLabel={(selected) => selected[0]?.label ?? ''}
      required={required}
      shouldUnregister={shouldUnregister}
      fullWidth
      searchable
      onSearchInputDebounceChange={setAddressInput}
      onSelectedChange={(selected) => {
        const suggestion = selected[0]?.object ?? null
        setUserSelectedAddress(suggestion)
        onAddressChange?.(suggestion)
      }}
    />
  )
}
